import { array, eq, number, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { get, isEqual } from 'lodash/fp';
import { Amount as GeneratedAmount, TaxBreakdown, TaxBreakdownItem } from '@mews-commander/data-access';
import { Amount, TaxValue } from '@mews-commander/types';
import { getItemByCommonPropertyValue, getNullableEq, gtZero } from '@mews/fp';
import {
    divideNumbersAsDecimals,
    multiplyNumbersAsDecimals,
    sumMultipleNumbersAsDecimals,
    sumNumbersAsDecimals,
} from '@mews/framework';

export const dummyAmount: Amount = {
    currency: '',
    grossValue: 0,
    netValue: 0,
    value: 0,
    source: null,
};

export const sumAmounts = (prev: Amount, next: Amount): Amount => {
    const {
        netValue: prevNetValue,
        grossValue: prevGrossValue,
        value: prevValue,
        currency: prevCurrencyCode,
        source: prevSource,
    } = prev;
    const {
        netValue: nextNetValue,
        grossValue: nextGrossValue,
        value: nextValue,
        currency: nextCurrencyCode,
        source: nextSource,
    } = next;

    return {
        netValue: sumNumbersAsDecimals(prevNetValue, nextNetValue),
        value: sumNumbersAsDecimals(prevValue, nextValue),
        grossValue: sumNumbersAsDecimals(prevGrossValue, nextGrossValue),
        currency: prevCurrencyCode || nextCurrencyCode,
        source: sumAmountSources(prevSource, nextSource),
    };
};

const amountsCommonCurrency = getItemByCommonPropertyValue<Amount, 'currency'>('currency');
export const sumAllAmounts = (amounts: Array<Amount>, defaultCurrencyCode = dummyAmount.currency): Amount => ({
    netValue: sumMultipleNumbersAsDecimals(amounts.map(({ netValue }) => netValue)),
    value: sumMultipleNumbersAsDecimals(amounts.map(({ value }) => value)),
    grossValue: sumMultipleNumbersAsDecimals(amounts.map(({ grossValue }) => grossValue)),
    currency: amounts.map(({ currency }) => currency).find(Boolean) ?? defaultCurrencyCode,
    source: pipe(
        amounts,
        array.map(({ source }) => source),
        (sources) =>
            sources.every((source) => source != null) && amountsCommonCurrency(sources) != null
                ? sumAllAmounts(sources)
                : null
    ),
});

export const sumAllAmountsValues = (amounts: Array<Pick<GeneratedAmount, 'currency' | 'value'>>) => ({
    currency: amounts.find(({ currency }) => !!currency)?.currency ?? dummyAmount.currency,
    value: sumMultipleNumbersAsDecimals(amounts.map(({ value }) => value)),
});

export const multiplyAmount = (amount: Amount, multiplier: number): Amount => {
    const { netValue, grossValue, value, currency } = amount;

    return {
        netValue: multiplyNumbersAsDecimals(netValue, multiplier),
        value: multiplyNumbersAsDecimals(value, multiplier),
        grossValue: multiplyNumbersAsDecimals(grossValue, multiplier),
        source: amount.source ? multiplyAmount(amount.source, multiplier) : null,
        currency,
    };
};

export const divideAmount = (amount: Amount, divisor: number): Amount => {
    const { netValue, grossValue, value, currency } = amount;

    return {
        netValue: divideNumbersAsDecimals(netValue, divisor),
        value: divideNumbersAsDecimals(value, divisor),
        grossValue: divideNumbersAsDecimals(grossValue, divisor),
        source: amount.source ? divideAmount(amount.source, divisor) : null,
        currency,
    };
};

const getAmountValue = get<Amount, 'value'>('value');

export const isAmountPositive = flow(getAmountValue, gtZero);

export const isZeroAmount = flow(getAmountValue, isEqual(0));

export const getPositiveAmount = ({ value, grossValue, netValue, currency, source }: Amount): Amount => {
    const positiveValue = Math.abs(value);
    const positiveGrossValue = Math.abs(grossValue);
    const positiveNetValue = Math.abs(netValue);

    return {
        value: positiveValue,
        grossValue: positiveGrossValue,
        netValue: positiveNetValue,
        currency,
        source: source && getPositiveAmount(source),
    };
};

export const getNegativeAmount = ({ value, grossValue, netValue, currency, source }: Amount): Amount => {
    const negativeValue = Math.abs(value) * -1;
    const negativeGrossValue = Math.abs(grossValue) * -1;
    const negativeNetValue = Math.abs(netValue) * -1;
    return {
        value: negativeValue,
        grossValue: negativeGrossValue,
        netValue: negativeNetValue,
        currency,
        source: source && getNegativeAmount(source),
    };
};

export const safeSumAmounts = (first: Amount, second: Amount) =>
    sumAmounts(first || dummyAmount, second || dummyAmount);

const sumAmountSources = (first: Amount | null, second: Amount | null) =>
    first && second && !(first.currency && first.currency !== second.currency) ? sumAmounts(first, second) : null;

const eqTaxValue = eq.struct<TaxValue>({ taxRateCode: string.Eq, value: number.Eq });
const eqAmountTaxValue = getNullableEq(array.getEq(eqTaxValue));

export const eqAmount: eq.Eq<GeneratedAmount> = eq.struct<GeneratedAmount>({
    currency: string.Eq,
    value: number.Eq,
    grossValue: getNullableEq(number.Eq),
    netValue: getNullableEq(number.Eq),
    taxValues: eqAmountTaxValue,
    breakdown: getNullableEq(
        eq.struct<TaxBreakdown>({
            items: getNullableEq(
                array.getEq(
                    eq.struct<TaxBreakdownItem>({
                        netValue: number.Eq,
                        taxValue: number.Eq,
                        taxRateCode: getNullableEq(string.Eq),
                    })
                )
            ),
        })
    ),
    taxRate: getNullableEq(number.Eq),
    before: getNullableEq(eq.fromEquals((x, y) => eqAmount.equals(x, y))),
    source: getNullableEq(eq.fromEquals((x, y) => eqAmount.equals(x, y))),
});

export function areAmountsEqual(first: Amount, second: Amount): boolean {
    return (
        first.currency === second.currency &&
        first.grossValue === second.grossValue &&
        first.netValue === second.netValue &&
        first.value === second.value &&
        eqAmountTaxValue.equals(first.taxValues, second.taxValues) &&
        ((first.source && second.source && areAmountsEqual(first.source, second.source)) ||
            first.source === second.source)
    );
}
